import { LandingPage, landingHead } from '@/features/landing/landing-page'
import { authClient } from '@/lib/auth'
import { SESSION_HINT_REDIRECT_SCRIPT, hasSessionHint, readCookieHeader } from '@/lib/session-hint'
import { Navigate, createFileRoute, redirect } from '@tanstack/react-router'

/**
 * The site's front door. A visitor gets the public page — server-rendered, so
 * crawlers and first-time visitors get the content itself rather than a redirect
 * to it. Someone already signed in who has just arrived is sent on to their
 * dashboard, so an existing customer typing the bare domain lands in the product
 * rather than on a sales pitch.
 *
 * The landing page is NOT wrapped in `<SignedOut>`. That gate renders nothing on
 * the server — the Neon session never reaches it — so the whole page would have
 * been missing from the served HTML and only appear after hydration: nothing for
 * a crawler to read, and a blank first paint for every visitor.
 */

/** Where the parser-installed listener records that the visitor did something. */
const ENGAGED_FLAG = '__beesummerEngaged'

/**
 * Runs while the HTML is still parsing, before React exists.
 *
 * Both things this page waits on — the session and hydration — land around the
 * same moment, several hundred milliseconds in. A listener attached from a React
 * effect is therefore not reliably there for the scroll it needs to observe;
 * measured, it missed every one. This listener is attached by the parser, so it
 * cannot be late.
 */
const ENGAGEMENT_SCRIPT = `(function(){var m=function(){window.${ENGAGED_FLAG}=1};
['scroll','wheel','touchstart','pointerdown','keydown'].forEach(function(e){
addEventListener(e,m,{once:true,passive:true})});if(scrollY>0)m()})()`

export const Route = createFileRoute('/')({
  // A returning customer goes straight to the product. A full page load of `/`
  // is the prerendered file, so that case is SESSION_HINT_REDIRECT_SCRIPT in
  // <head>; this beforeLoad covers client-side navigation to `/` (and dev SSR).
  // DashboardHandoff below still covers the first visit after signing in on a
  // browser that has no hint yet.
  beforeLoad: () => {
    if (hasSessionHint(readCookieHeader())) throw redirect({ to: '/dashboard' })
  },
  head: () => {
    const head = landingHead()
    return {
      ...head,
      scripts: [
        { children: SESSION_HINT_REDIRECT_SCRIPT },
        ...(head.scripts ?? []),
        { children: ENGAGEMENT_SCRIPT },
      ],
    }
  },
  component: Home,
})

function Home() {
  return (
    <>
      <DashboardHandoff />
      <LandingPage />
    </>
  )
}

/**
 * Sends an arriving customer to the product — but only if they are still
 * arriving.
 *
 * Firing unconditionally yanked the page out from under anyone who had started
 * reading while the session was still resolving: you scroll into the product
 * tour and the browser navigates away mid-scroll.
 *
 * The flag is read here, at render, rather than mirrored into React state. This
 * component mounts at exactly one moment — when the session resolves — and that
 * is the moment the decision is actually being made, so the freshest possible
 * answer is the one read on the spot. Holding it in state instead meant the
 * value was captured before the interaction happened and never caught up.
 *
 * Reading `window` during render is safe here specifically because the session
 * has no data on the server, so this returns before reaching it there, and
 * `<Navigate>` renders no markup either way.
 */
function DashboardHandoff() {
  // authClient 的 session hook，不用 <SignedIn>：那要整包 auth UI provider，行銷頁不載。
  // 伺服器端與確認中都沒有 data，跟原本 <SignedIn> 一樣不 render。
  const { data } = authClient.useSession()
  if (!data) return null
  if (typeof window !== 'undefined' && (window as unknown as EngagementWindow)[ENGAGED_FLAG]) {
    return null
  }
  return <Navigate to="/dashboard" />
}

type EngagementWindow = { [ENGAGED_FLAG]?: 1 }
